/**
 * Reusable Range Filter Dropdown Component
 * Used for filtering numeric ranges (amount, billing progress, etc.)
 * Position via useFilterPopupPortal (parent) — do not self-position.
 */
import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { FILTER_POPUP_CLASS } from '../../../hooks/useFilterPopupPortal';

export const RangeFilterDropdown = ({
  isOpen,
  onApply,
  onClose,
  currentRange,
  label = 'Filter by Range',
  minLabel = 'Minimum Value',
  maxLabel = 'Maximum Value',
  minPlaceholder = 'Min',
  maxPlaceholder = 'Max',
  formatValue = null,
}) => {
  const [minValue, setMinValue] = useState(currentRange?.min ?? '');
  const [maxValue, setMaxValue] = useState(currentRange?.max ?? '');

  useEffect(() => {
    if (isOpen) {
      setMinValue(currentRange?.min ?? '');
      setMaxValue(currentRange?.max ?? '');
    }
  }, [isOpen, currentRange]);

  // UX: no auto-close on outside tap / scroll; close via X / Clear / Apply.

  if (!isOpen) return null;

  const handleApply = () => {
    const min = minValue === '' ? null : Number(minValue);
    const max = maxValue === '' ? null : Number(maxValue);
    onApply({ min, max });
    onClose();
  };

  const handleClear = () => {
    setMinValue('');
    setMaxValue('');
    onApply({ min: null, max: null });
    onClose();
  };

  const defaultFormatValue = (value) => {
    if (value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const displayFormat = formatValue || defaultFormatValue;

  return (
    <div
      className={`${FILTER_POPUP_CLASS} z-50 bg-white border rounded-md shadow-lg w-full`}
      style={{ maxWidth: '20rem' }}
    >
      <div className="flex flex-col">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b bg-white px-3 py-2">
          <div className="text-sm font-medium text-gray-700">{label}</div>
          <button
            type="button"
            aria-label="Close filter"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
          >
            <FiX className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        <div className="p-4 pt-3">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">{minLabel}</label>
              <input
                type="number"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                placeholder={minPlaceholder}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
              {minValue !== '' && (
                <div className="text-xs text-gray-500 mt-1">{displayFormat(minValue)}</div>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">{maxLabel}</label>
              <input
                type="number"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                placeholder={maxPlaceholder}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500"
              />
              {maxValue !== '' && (
                <div className="text-xs text-gray-500 mt-1">{displayFormat(maxValue)}</div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white p-2 border-t">
          <div className="flex justify-end gap-2">
            <button
              onClick={handleClear}
              className="px-4 py-1.5 text-sm font-normal border rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 text-sm font-normal bg-red-600 text-white rounded hover:bg-red-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
